import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { makeAPIRequestReactQuery } from '../api';
import { endpoints } from '../endpoints';

export const useGetPracticeGroups = () => {
  const { primaryOrganizationId: orgId } = useSelector(
    (state: any) => state.login.user,
  );

  const practiceGroupsPath = `${endpoints.practiceGroups}/${orgId}/`;

  return useQuery([practiceGroupsPath, orgId], () =>
    makeAPIRequestReactQuery<{ data: TPracticeGroup[] }>(
      practiceGroupsPath,
      {},
      'get',
      {
        headers: {
          Org: orgId,
        },
      },
    ),
  );
};

export const useCreatePracticeGroup = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { primaryOrganizationId: orgId } = useSelector(
    (state: any) => state.login.user,
  );

  const practiceGroupsPath = `${endpoints.practiceGroups}/${orgId}/`;

  return useMutation({
    mutationFn: async (data: { name: string; practices: string[] }) => {
      const { name, practices } = data;

      await makeAPIRequestReactQuery(practiceGroupsPath, {}, 'POST', {
        headers: {
          Org: orgId,
        },
        data: {
          name: name,
          practices: practices,
        },
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([practiceGroupsPath]);
      onSuccess?.();
    },
  });
};

export const useUpdatePracticeGroup = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { primaryOrganizationId: orgId } = useSelector(
    (state: any) => state.login.user,
  );

  const practiceGroupsPath = `${endpoints.practiceGroups}/${orgId}/`;

  return useMutation({
    mutationFn: async (data: {
      id: string;
      name: string;
      practices: string[];
      active?: boolean;
    }) => {
      const { id, name, practices, active: isActive } = data;

      await makeAPIRequestReactQuery(practiceGroupsPath, {}, 'PUT', {
        headers: {
          Org: orgId,
        },
        data: {
          practiceGroupId: id,
          name: name,
          practices: practices,
          active: isActive,
        },
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([practiceGroupsPath]);
      onSuccess?.();
    },
  });
};

export const useDeletePracticeGroup = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { primaryOrganizationId: orgId } = useSelector(
    (state: any) => state.login.user,
  );

  const practiceGroupsPath = `${endpoints.practiceGroups}/${orgId}/`;

  return useMutation({
    mutationFn: async (data: { practiceGroupId: string }) => {
      const { practiceGroupId } = data;

      await makeAPIRequestReactQuery(
        practiceGroupsPath,
        {
          practiceGroupId: practiceGroupId,
        },
        'DELETE',
        {
          headers: {
            Org: orgId,
          },
        },
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([practiceGroupsPath]);
      onSuccess?.();
    },
  });
};

export type TPracticeGroup = {
  id: string;
  name: string;
  practices: {
    practiceId: number;
    practiceVideaId: string;
    practiceName: string;
  }[];
  active: boolean | null;
  updatedBy: string | null;
  updatedAt: string | null;
};
